<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue'
import { decode } from 'blurhash'

const props = withDefaults(defineProps<{
  src: string
  alt: string
  hash: string
  width?: number
  height?: number
  objectFit?: 'cover' | 'contain' | 'fill'
}>(), {
  objectFit: 'cover'
})

const emit = defineEmits<{
  load: []
}>()

const isLoaded = ref(false)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const imgRef = ref<HTMLImageElement | null>(null)

// 当图片加载完成时调用
const handleLoad = () => {
  if (!isLoaded.value) {
    isLoaded.value = true
    emit('load')
  }
}

onMounted(() => {
  // 渲染 BlurHash，使用更高分辨率以显示更多细节
  if (props.hash && canvasRef.value) {
    try {
      const pixels = decode(props.hash, 128, 128)
      const ctx = canvasRef.value.getContext('2d')
      if (ctx) {
        const imageData = ctx.createImageData(128, 128)
        imageData.data.set(pixels)
        ctx.putImageData(imageData, 0, 0)
      }
    } catch (e) {
      console.warn('BlurhashImage: Failed to decode hash', e)
    }
  }
  
  // 关键：检查图片是否已经从缓存加载完成
  // 当图片被浏览器缓存时，load 事件可能在监听器绑定前就触发了
  nextTick(() => {
    if (imgRef.value && imgRef.value.complete && imgRef.value.naturalWidth > 0) {
      handleLoad()
    }
  })
})
</script>

<template>
  <div class="blurhash-container">
    <canvas
      v-show="!isLoaded"
      ref="canvasRef"
      width="128"
      height="128"
      class="placeholder-canvas"
    ></canvas>

    <img
      ref="imgRef"
      :src="src"
      :alt="alt"
      loading="lazy"
      class="actual-image"
      :class="{ 'is-visible': isLoaded }"
      :style="{ objectFit: objectFit }"
      @load="handleLoad"
    />
  </div>
</template>

<style scoped>
.blurhash-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
}

.placeholder-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* 加上高斯模糊让 Canvas 看起来更平滑 */
  filter: blur(10px);
  transform: scale(1.1); /* 稍微放大一点点隐藏边缘黑边 */
}

.actual-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.4s ease-in-out;
}

.actual-image.is-visible {
  opacity: 1;
}
</style>